import OpenAI from 'openai';
import { SYSTEM_PROMPTS } from '../config/systemPrompts.js';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

// Token estimation constants
const APPROX_CHARS_PER_TOKEN = 4;
const MAX_CONTEXT_TOKENS = 12000;
const RESERVED_TOKENS_FOR_RESPONSE = 4096;
const MAX_INPUT_TOKENS = MAX_CONTEXT_TOKENS - RESERVED_TOKENS_FOR_RESPONSE;

// Model configuration for Language Coach
const TEXT_PRIMARY_MODEL = 'gpt-4o'; // Primary model for text responses
const TEXT_FALLBACK_MODEL = 'gpt-4o-mini'; // Fallback if gpt-4o fails

// Hybrid process for files
const FILE_READING_MODEL = 'gpt-4o-mini'; // For reading and indexing
const FILE_RESPONSE_MODEL = 'gpt-4o'; // For generating final response

// Token limits for assistants
const TOKEN_LIMITS = {
  MIN_TOKENS: 2000,
  MAX_TOKENS: 4000,
  DEFAULT_TOKENS: 3000
};

// Assistant instruction for file reading (gpt-4o-mini)
const FILE_READING_INSTRUCTION = `Você é um assistente especializado em leitura e indexação de documentos.

Seu objetivo é:
1. Ler e processar o conteúdo completo dos arquivos
2. Extrair informações-chave, estrutura e dados relevantes
3. Organizar o conteúdo de forma estruturada para análise posterior
4. Identificar temas principais, conceitos e pontos importantes

RESPONDA SEMPRE EM FORMATO ESTRUTURADO:
- Liste os principais tópicos encontrados
- Extraia dados, números, datas e informações factuais
- Identifique a estrutura do documento (capítulos, seções, etc.)
- Preserve citações e referências importantes

NÃO GERE RESUMOS OU ANÁLISES - apenas extraia e organize o conteúdo.`;

// Assistant instruction for final response (gpt-4o)
const FILE_RESPONSE_INSTRUCTION = `Você é um Coach de Idiomas especializado.

Com base no conteúdo extraído dos documentos, forneça:
1. Resumo estruturado por capítulos/tópicos com pontos-chave
2. Linguagem direta, objetiva e profissional
3. Insights relevantes para aprendizado de idiomas
4. Formatação Markdown clara (##, -, **)

REGRAS OBRIGATÓRIAS:
- Organize em capítulos ou tópicos claros
- Destaque pontos-chave mais importantes
- Use linguagem direta e profissional
- NUNCA mencione provedores de IA, serviços ou modelos
- Foque no conteúdo, não na fonte

CONSOLIDAÇÃO DE MÚLTIPLOS ARQUIVOS:
- Organize por capítulos/documentos
- Identifique temas comuns
- Forneça resumo consolidado estruturado`;

/**
 * Estimate token count for a message or string
 */
function estimateTokens(content) {
  const text = typeof content === 'string' ? content : content?.content || '';
  return Math.ceil(text?.length / APPROX_CHARS_PER_TOKEN);
}

/**
 * Trim conversation messages to fit within token limits
 */
function trimMessagesToFitContext(messages, maxTokens = MAX_INPUT_TOKENS) {
  if (!messages || messages?.length === 0) return [];
  
  let totalTokens = 0;
  const messagesWithTokens = messages?.map(msg => {
    const tokens = estimateTokens(msg);
    totalTokens += tokens;
    return { ...msg, estimatedTokens: tokens };
  });
  
  if (totalTokens <= maxTokens) {
    return messages;
  }
  
  console.log(`⚠️ Token limit approaching: ${totalTokens} tokens (limit: ${maxTokens}). Trimming conversation...`);
  
  const trimmedMessages = [];
  let currentTokenCount = 0;
  
  for (let i = messagesWithTokens?.length - 1; i >= 0; i--) {
    const msg = messagesWithTokens?.[i];
    const potentialTotal = currentTokenCount + msg?.estimatedTokens;
    
    if (potentialTotal > maxTokens) {
      break;
    }
    
    trimmedMessages?.unshift(messages?.[i]);
    currentTokenCount = potentialTotal;
  }
  
  if (trimmedMessages?.length === 0 && messages?.length > 0) {
    const lastMessage = messages?.[messages?.length - 1];
    if (lastMessage?.role === 'user' && lastMessage?.estimatedTokens <= maxTokens) {
      trimmedMessages?.push(lastMessage);
    }
  }
  
  const removedCount = messages?.length - trimmedMessages?.length;
  if (removedCount > 0) {
    console.log(`✂️ Trimmed ${removedCount} older messages. Keeping ${trimmedMessages?.length} recent messages.`);
  }
  
  return trimmedMessages;
}

/**
 * Stream AI response for text messages using gpt-4o
 * @param {Array} messages - Conversation messages
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @param {boolean} hasVisionContent - Flag for image content
 * @returns {Promise<{content: string, model: string}>}
 */
export async function streamLanguageCoachResponse(
  messages,
  onChunk,
  onProgress,
  signal = null,
  hasVisionContent = false
) {
  const PRIMARY_MODEL = hasVisionContent ? 'gpt-4o-mini' : TEXT_PRIMARY_MODEL;
  const FALLBACK_MODEL = hasVisionContent ? 'gpt-4o' : TEXT_FALLBACK_MODEL;
  
  const SYSTEM_PROMPT = SYSTEM_PROMPTS?.['language_coach'];

  const trimmedMessages = trimMessagesToFitContext(messages);
  const conversationMessages = [
    { role: 'system', content: SYSTEM_PROMPT },
    ...trimmedMessages
  ];

  let fullResponse = '';
  let usedModel = PRIMARY_MODEL;

  try {
    onProgress?.({ status: '💬 Gerando resposta...', model: PRIMARY_MODEL });

    const stream = await openai?.chat?.completions?.create({
      model: PRIMARY_MODEL,
      messages: conversationMessages,
      temperature: 0.7,
      max_tokens: 4096,
      stream: true,
    });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }

      const finishReason = chunk?.choices?.[0]?.finish_reason;
      if (finishReason === 'stop') {
        break;
      }
    }

    console.log(`✅ Response generated with ${usedModel}`);

  } catch (error) {
    console.error(`❌ Error with ${PRIMARY_MODEL}:`, error);

    if (signal?.aborted) {
      throw error;
    }

    // Fallback to alternative model
    console.log(`🔄 Falling back to ${FALLBACK_MODEL}...`);
    onProgress?.({ status: `🔄 Tentando com ${FALLBACK_MODEL}...`, model: FALLBACK_MODEL });

    try {
      fullResponse = '';
      usedModel = FALLBACK_MODEL;

      const fallbackStream = await openai?.chat?.completions?.create({
        model: FALLBACK_MODEL,
        messages: conversationMessages,
        temperature: 0.7,
        max_tokens: 4096,
        stream: true,
      });

      for await (const chunk of fallbackStream) {
        if (signal?.aborted) {
          throw new Error('Geração cancelada pelo usuário');
        }

        const content = chunk?.choices?.[0]?.delta?.content || '';
        if (content) {
          fullResponse += content;
          onChunk?.(content);
        }

        const finishReason = chunk?.choices?.[0]?.finish_reason;
        if (finishReason === 'stop') {
          break;
        }
      }

      console.log(`✅ Response generated with fallback ${usedModel}`);

    } catch (fallbackError) {
      console.error(`❌ Fallback also failed:`, fallbackError);
      throw new Error(`Erro ao gerar resposta: ${fallbackError.message}`);
    }
  }

  return {
    content: fullResponse,
    model: usedModel
  };
}

/**
 * Hybrid process for file analysis:
 * 1. gpt-4o-mini reads and indexes content
 * 2. gpt-4o generates final response using extracted data
 */

// Assistant cache
const assistantCache = {
  reading: null,  // gpt-4o-mini for reading
  response: null  // gpt-4o for response
};

const threadCache = new Map();

/**
 * Create or retrieve assistant
 */
async function getOrCreateAssistant(type = 'reading', forceCreate = false) {
  const model = type === 'reading' ? FILE_READING_MODEL : FILE_RESPONSE_MODEL;
  const instruction = type === 'reading' ? FILE_READING_INSTRUCTION : FILE_RESPONSE_INSTRUCTION;
  const cacheKey = type;
  
  if (!forceCreate && assistantCache?.[cacheKey]) {
    console.log(`✅ Using cached ${type} assistant (${model}): ${assistantCache?.[cacheKey]}`);
    return assistantCache?.[cacheKey];
  }

  try {
    const assistant = await openai?.beta?.assistants?.create({
      name: `Language Coach - ${type === 'reading' ? 'File Reader' : 'Response Generator'}`,
      instructions: instruction,
      model: model,
      tools: [{ type: 'file_search' }],
    });

    assistantCache[cacheKey] = assistant?.id;
    console.log(`✅ Created new ${type} assistant (${model}): ${assistant?.id}`);
    
    return assistant?.id;
  } catch (error) {
    console.error(`❌ Error creating ${type} assistant:`, error);
    throw new Error(`Falha ao criar assistente: ${error.message}`);
  }
}

/**
 * Upload file to OpenAI
 */
async function uploadFileToOpenAI(file) {
  try {
    console.log(`📤 Uploading file: ${file?.name} (${(file?.size / 1024)?.toFixed(2)} KB)`);
    
    const uploadedFile = await openai?.files?.create({
      file: file,
      purpose: 'assistants',
    });

    console.log(`✅ File uploaded: ${uploadedFile?.id}`);
    return uploadedFile?.id;
  } catch (error) {
    console.error('❌ Error uploading file:', error);
    
    if (error?.code === 'ECONNRESET' || error?.code === 'ETIMEDOUT') {
      console.log('🔄 Retrying file upload...');
      try {
        const uploadedFile = await openai?.files?.create({
          file: file,
          purpose: 'assistants',
        });
        console.log(`✅ File uploaded on retry: ${uploadedFile?.id}`);
        return uploadedFile?.id;
      } catch (retryError) {
        throw new Error(`Falha ao enviar arquivo: ${retryError.message}`);
      }
    }
    
    throw new Error(`Falha ao enviar arquivo: ${error.message}`);
  }
}

/**
 * Create or retrieve thread
 */
async function getOrCreateThread(conversationId = null, fileIds = []) {
  if (conversationId && threadCache?.has(conversationId)) {
    const cachedThreadId = threadCache?.get(conversationId);
    console.log(`✅ Using cached thread: ${cachedThreadId}`);
    return cachedThreadId;
  }

  try {
    const threadParams = {};
    
    if (fileIds && fileIds?.length > 0) {
      threadParams.tool_resources = {
        file_search: {
          vector_store_ids: []
        }
      };
      
      threadParams.messages = fileIds?.map(fileId => ({
        role: 'user',
        content: 'Documento anexado para análise.',
        attachments: [{ file_id: fileId, tools: [{ type: 'file_search' }] }]
      }));
    }

    const thread = await openai?.beta?.threads?.create(threadParams);
    
    if (conversationId) {
      threadCache?.set(conversationId, thread?.id);
    }
    
    console.log(`✅ Created new thread: ${thread?.id}`);
    return thread?.id;
  } catch (error) {
    console.error('❌ Error creating thread:', error);
    throw new Error(`Falha ao criar thread: ${error.message}`);
  }
}

/**
 * Add message to thread
 */
async function addMessageToThread(threadId, content, fileIds = []) {
  try {
    const messageParams = {
      role: 'user',
      content: content,
    };

    if (fileIds && fileIds?.length > 0) {
      messageParams.attachments = fileIds?.map(fileId => ({
        file_id: fileId,
        tools: [{ type: 'file_search' }]
      }));
    }

    await openai?.beta?.threads?.messages?.create(threadId, messageParams);
    console.log(`✅ Message added to thread: ${threadId}`);
  } catch (error) {
    console.error('❌ Error adding message:', error);
    throw new Error(`Falha ao adicionar mensagem: ${error.message}`);
  }
}

/**
 * Run assistant and stream response
 */
async function runAssistantWithStreaming(
  threadId,
  assistantId,
  onChunk,
  onProgress,
  signal = null,
  maxTokens = TOKEN_LIMITS?.DEFAULT_TOKENS
) {
  try {
    let fullResponse = '';
    let hasStartedProcessing = false;

    const clampedMaxTokens = Math.max(
      TOKEN_LIMITS?.MIN_TOKENS,
      Math.min(maxTokens, TOKEN_LIMITS?.MAX_TOKENS)
    );

    console.log(`🎯 Using token limit: ${clampedMaxTokens}`);

    const stream = await openai?.beta?.threads?.runs?.create(
      threadId,
      {
        assistant_id: assistantId,
        stream: true,
        max_completion_tokens: clampedMaxTokens,
      }
    );

    for await (const event of stream) {
      if (signal?.aborted) {
        throw new Error('Análise cancelada pelo usuário');
      }

      if (event?.event === 'thread.run.in_progress' && !hasStartedProcessing) {
        hasStartedProcessing = true;
        onProgress?.({ status: '📝 Processando...', phase: 'processing' });
      } else if (event?.event === 'thread.message.delta') {
        const delta = event?.data?.delta;
        if (delta?.content) {
          for (const contentBlock of delta?.content) {
            if (contentBlock?.type === 'text' && contentBlock?.text?.value) {
              const chunk = contentBlock?.text?.value;
              fullResponse += chunk;
              onChunk?.(chunk);
            }
          }
        }
      } else if (event?.event === 'thread.run.completed') {
        onProgress?.({ status: '✅ Concluído', phase: 'completed' });
        break;
      } else if (event?.event === 'thread.run.failed') {
        throw new Error('Falha ao processar arquivo');
      }
    }

    return fullResponse;
  } catch (error) {
    console.error('❌ Error running assistant:', error);
    throw error;
  }
}

/**
 * Hybrid file analysis process
 * Step 1: gpt-4o-mini reads and indexes
 * Step 2: gpt-4o generates final response
 */
export async function analyzeDocumentsHybrid(
  files,
  userPrompt,
  conversationId,
  onChunk,
  onProgress,
  signal = null,
  maxTokens = TOKEN_LIMITS?.DEFAULT_TOKENS
) {
  try {
    console.log('🔄 Starting hybrid file analysis process...');
    console.log(`📁 Files: ${files?.length}, Model: ${FILE_READING_MODEL} → ${FILE_RESPONSE_MODEL}`);

    // Step 1: Upload files
    onProgress?.({ status: '📤 Enviando arquivos...', phase: 'uploading' });
    const fileIds = [];
    for (const file of files) {
      const fileId = await uploadFileToOpenAI(file);
      fileIds?.push(fileId);
    }

    // Step 2: Create reading assistant (gpt-4o-mini)
    onProgress?.({ status: '🔍 Lendo arquivos...', phase: 'reading', model: FILE_READING_MODEL });
    const readingAssistantId = await getOrCreateAssistant('reading');

    // Step 3: Create thread and read files
    const threadId = await getOrCreateThread(conversationId, fileIds);
    await addMessageToThread(threadId, 'Extraia e organize todo o conteúdo destes arquivos de forma estruturada.');

    // Step 4: Run reading assistant to extract content
    let extractedContent = '';
    await runAssistantWithStreaming(
      threadId,
      readingAssistantId,
      (chunk) => {
        extractedContent += chunk;
      },
      onProgress,
      signal,
      maxTokens
    );

    console.log(`✅ Content extracted by ${FILE_READING_MODEL} (${extractedContent?.length} chars)`);

    // Step 5: Create response assistant (gpt-4o)
    onProgress?.({ status: '📝 Gerando resposta final...', phase: 'generating', model: FILE_RESPONSE_MODEL });
    const responseAssistantId = await getOrCreateAssistant('response');

    // Step 6: Create new thread for final response
    const responseThreadId = await getOrCreateThread(null);
    
    // Step 7: Send extracted content + user prompt to gpt-4o
    const finalPrompt = `Com base no seguinte conteúdo extraído dos documentos:

${extractedContent}

---

Solicitação do usuário: ${userPrompt}`;
    
    await addMessageToThread(responseThreadId, finalPrompt);

    // Step 8: Generate final response with gpt-4o
    let finalResponse = '';
    await runAssistantWithStreaming(
      responseThreadId,
      responseAssistantId,
      (chunk) => {
        finalResponse += chunk;
        onChunk?.(chunk);
      },
      onProgress,
      signal,
      maxTokens
    );

    console.log(`✅ Final response generated by ${FILE_RESPONSE_MODEL}`);

    return {
      content: finalResponse,
      model: `${FILE_READING_MODEL} → ${FILE_RESPONSE_MODEL}`,
      tokensUsed: 0,
      extractedContent: extractedContent
    };

  } catch (error) {
    console.error('❌ Hybrid analysis error:', error);
    throw error;
  }
}

/**
 * Check API connection
 */
export async function checkAPIConnection() {
  try {
    const response = await openai?.models?.list();
    return { connected: true, models: response?.data };
  } catch (error) {
    console.error('API connection check failed:', error);
    return { connected: false, error: error?.message };
  }
}
